import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Department, Skill } from '@lib/interfaces';
import { CourseCommentsResponse } from '@lib/interfaces/course-comments.interface';
import {
    CourseFilter,
    CourseSearchResponse,
    CoursesForProgramResponse,
    CourseStatusCounts,
    MediaUpload,
    MyCourse,
} from '@lib/interfaces/course.interface';
import { CourseEnrollmentTypeResponse, EnrollmentTypeResponse } from '@lib/interfaces/program.interface';
import { formatToLocalDateTime } from '@lib/utils';
import { BehaviorSubject, map, Observable, shareReplay } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CourseService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;
    private programsCache$: Observable<any> | null = null;

    public courseTopics: BehaviorSubject<Skill[]> = new BehaviorSubject<Skill[]>([]);
    public courseDepartments: BehaviorSubject<Department | null> = new BehaviorSubject<Department | null>(null);
    public courseCreditsFile: BehaviorSubject<MediaUpload | null> = new BehaviorSubject<MediaUpload | null>(null);
    public courseMarketingBrochure: BehaviorSubject<MediaUpload | null> = new BehaviorSubject<MediaUpload | null>(null);
    public courseNewLocation: BehaviorSubject<{ id: string; name: string } | null> = new BehaviorSubject<{
        id: string;
        name: string;
    } | null>(null);

    createOrUpdateCourse(payload: any): Observable<any> {
        return this.http.patch(`${this.baseUrl}/course/courses`, payload);
    }

    updateCourseDetails(payload: any): Observable<any> {
        return this.http.put(`${this.baseUrl}/courses/details`, payload);
    }

    updateCourseFees(payload: any): Observable<any> {
        return this.http.put(`${this.baseUrl}/courses/fees`, payload);
    }

    uploadMedia(file: File, type: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        return this.http.post(`${this.baseUrl}/media/upload`, formData);
    }

    getDepartments(searchParam?: string, page: number = 0, size: number = 10): Observable<any> {
        let params = new HttpParams();
        if (searchParam) {
            params = params.set('searchParam', searchParam);
        }
        params = params.set('page', page.toString());
        params = params.set('size', size.toString());
        return this.http.get(`${this.baseUrl}/department`, { params });
    }

    getPrograms(): Observable<any> {
        if (!this.programsCache$) {
            this.programsCache$ = this.http.get(`${this.baseUrl}/program`).pipe(shareReplay(1));
        }
        return this.programsCache$;
    }

    getInHouseProgramId(): Observable<string | undefined> {
        return this.getPrograms().pipe(
            map((programs: { id: string; name: string }[]) => programs.find((p) => p.name === 'In House')?.id),
        );
    }

    getLocations(): Observable<{ data: any[]; totalRecords: number; errors: any }> {
        return this.http.get<{ data: any[]; totalRecords: number; errors: any }>(`${this.baseUrl}/location`);
    }

    getCourseTypes(): Observable<any> {
        return this.http.get(`${this.baseUrl}/enrollment-type`);
    }

    getTopics(searchParam?: string, page: number = 0, size: number = 10): Observable<any> {
        let params = new HttpParams();
        if (searchParam) {
            params = params.set('searchParam', searchParam);
        }
        params = params.set('page', page.toString());
        params = params.set('size', size.toString());

        return this.http.get(`${this.baseUrl}/topic`, { params });
    }

    getEnrollmentTypes(feeType: string = 'OPEN'): Observable<EnrollmentTypeResponse> {
        return this.http.get<EnrollmentTypeResponse>(`${this.baseUrl}/enrollment-type?feeType=${feeType}`);
    }

    getCourseEnrollmentTypes(
        courseId: string | number,
        filterByCourseId: boolean = false,
    ): Observable<CourseEnrollmentTypeResponse> {
        return this.http.get<CourseEnrollmentTypeResponse>(
            `${this.baseUrl}/enrollment-type/course/${courseId}?filterByCourseId=${filterByCourseId}`,
        );
    }

    submitCourse(courseId: number | string): Observable<any> {
        return this.http.post(`${this.baseUrl}/course/submit/${courseId}`, {});
    }

    publishCourse(courseId: number | string, isPublished: boolean): Observable<any> {
        const params = new HttpParams().set('isLive', isPublished);

        return this.http.post(`${this.baseUrl}/course/publish/${courseId}`, {}, { params });
    }

    getCoFaculty(searchParam: string = '', page: number = 0, size: number = 10): Observable<any> {
        let params = new HttpParams()
            .append('role', 'FACULTY')
            .append('role', 'HOD')
            .append('role', 'DEAN_FA')
            .append('role', 'DEAN_EO');
        if (searchParam) {
            params = params.set('searchParam', searchParam);
        }
        params = params.set('page', page.toString());
        params = params.set('size', size.toString());
        params = params.set('excludeMisc', 'true');
        return this.http.get(`${this.baseUrl}/user`, { params });
    }

    getAssistantFaculty(searchParam: string = '', page: number = 0, size: number = 10): Observable<any> {
        let params = new HttpParams().append('role', 'ASSISTANT');
        if (searchParam) {
            params = params.set('searchParam', searchParam);
        }
        params = params.set('page', page.toString());
        params = params.set('size', size.toString());
        params = params.set('excludeMisc', 'true');
        return this.http.get(`${this.baseUrl}/user`, { params });
    }

    getSources(): Observable<any> {
        return this.http.get(`${this.baseUrl}/source`);
    }

    getCoursePreview(courseId: number | string): Observable<any> {
        return this.http.get(`${this.baseUrl}/course/preview/${courseId}`);
    }

    getCoursesByStatus( // NOSONAR
        status: string,
        searchParams?: string,
        self: boolean = true,
        isParent: boolean = false,
        page: number = 0,
        size: number = 10,
        filter?: any,
    ): Observable<any> {
        let params = new HttpParams();
        if (status) {
            params = params.set('status', status);
        }
        if (searchParams) {
            params = params.set('search', searchParams);
        }
        params = params.set('self', self.toString());
        params = params.set('isParent', isParent);

        params = params.set('page', page.toString());
        params = params.set('size', size.toString());
        // Mandatory
        if (filter.status && filter.status.length > 0) {
            filter.status.forEach((s: string) => (params = params.append('status', s)));
        } else {
            throw new Error('Status is required');
        }

        // Optional
        if (filter.searchParam) params = params.set('searchParam', filter.searchParam);
        if (filter.isFeedbackForm !== undefined)
            params = params.set('isFeedbackForm', filter.isFeedbackForm.toString());
        if (filter.self !== undefined) // NOSONAR
            params = params.set('self', filter.self.toString()); // NOSONAR
        else params = params.set('self', 'true');
        if (filter.isParent !== undefined) // NOSONAR
            params = params.set('isParent', filter.isParent.toString()); // NOSONAR
        else params = params.set('isParent', 'false');
        if (filter.type) params = params.set('type', filter.type);
        if (filter.courseStartDate) params = params.set('courseStartDate', filter.courseStartDate);
        if (filter.courseEndDate) params = params.set('courseEndDate', filter.courseEndDate);
        if (filter.isPublished !== undefined) params = params.set('isPublished', filter.isPublished.toString());
        if (filter.page !== undefined) params = params.set('page', filter.page.toString());
        if (filter.size !== undefined) params = params.set('size', filter.size.toString());
        if (filter.sortField) params = params.set('sortField', filter.sortField);
        if (filter.sortDirection) params = params.set('sortDirection', filter.sortDirection);

        return this.http.get<MyCourse[]>(`${this.baseUrl}/course/courses/get-Course`, { params });
    }

    getCoursesByMultipleStatuses(options: {
        statuses: string[];
        searchParams?: string;
        isFeedbackForm?: 'Yes' | 'No' | 'All';
        page?: number;
        size?: number;
        isParent?: boolean;
    }): Observable<any> {
        let params = new HttpParams();

        if (options.statuses?.length > 0) {
            options.statuses.forEach((status) => {
                params = params.append('status', status);
            });
        }

        params = params.set('search', options.searchParams ?? '');
        params = params.set('isFeedbackForm', options.isFeedbackForm ?? 'No');
        params = params.set('page', options.page?.toString() ?? '0');
        params = params.set('size', options.size?.toString() ?? '10');
        params = params.set('isParent', options.isParent ?? false);

        return this.http.get<MyCourse[]>(`${this.baseUrl}/course/courses/get-Course`, { params });
    }

    getCoursesForProgram(options: { searchParams?: string; page?: number; size?: number }) {
        let params = new HttpParams();
        params = params.set('search', options.searchParams ?? '');
        params = params.set('page', options.page?.toString() ?? '0');
        params = params.set('size', options.size?.toString() ?? '10');
        params = params.set('excludeZeroCredits', 'true');

        return this.http.get<CoursesForProgramResponse>(`${this.baseUrl}/course/get-course-details`, { params });
    }

    searchCourses(options: {
        searchParams: string;
        self: boolean;
        isParent: boolean;
        enrollmentDashboard: boolean;
        page: number;
        size: number;
    }): Observable<any> {
        let params = new HttpParams();
        params = params.set('searchParam', options.searchParams);
        params = params.set('self', options.self.toString());
        params = params.set('isParent', options.isParent.toString());
        params = params.set('enrollmentDashboard', options.enrollmentDashboard.toString());
        params = params.set('page', options.page.toString());
        params = params.set('size', options.size.toString());
        return this.http.get<CourseSearchResponse[]>(`${this.baseUrl}/course`, { params });
    }

    downloadFileByUrl(fileUrl: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/file-system/${fileUrl}`, {
            responseType: 'blob',
        });
    }

    getCourseStatusCounts(options?: { // NOSONAR
        self?: boolean;
        type?: string;
        published?: string;
        isParent?: boolean | string;
        feedbackFormStatus?: string;
        searchParam?: string;
        isPublished?: string[];
        isFeedbackForm?: string[];
        courseStartDate?: string;
        courseEndDate?: string;
    }): Observable<CourseStatusCounts> {
        let params = new HttpParams();
        if (options) {
            params = params.set('self', options.self !== undefined ? options.self : true); // NOSONAR
            if (options.type) params = params.set('type', options.type);
            if (options.published) params = params.set('published', options.published);
            if (options.isParent !== undefined) params = params.set('isParent', String(options.isParent));
            if (options.courseStartDate) params = params.set('courseStartDate', options.courseStartDate);
            if (options.courseEndDate) params = params.set('courseEndDate', options.courseEndDate);
            if (options.feedbackFormStatus) params = params.set('feedbackFormStatus', options.feedbackFormStatus);
            if (options.searchParam) params = params.set('searchParam', options.searchParam);
            if (options.isPublished)
                options.isPublished.forEach((s: string) => (params = params.append('isPublished', s)));
            if (options.isFeedbackForm)
                options.isFeedbackForm.forEach((s: string) => (params = params.append('isFeedbackForm', s)));
        }
        return this.http.get<CourseStatusCounts>(`${this.baseUrl}/course/status-counts`, { params });
    }

    deleteMediaFile(mediaId: number): Observable<any> {
        return this.http.delete(`${this.baseUrl}/media-files/${mediaId}`);
    }

    getCourseProposal(courseId: number | string): Observable<any> {
        return this.http.get(`${this.baseUrl}/proposals/course/${courseId}`);
    }

    /**
     * Gets comments for a specific urcourse
     * @param courseId The ID of the course to get comments for
     * @returns Observable with course comments
     */
    getCourseComments(courseId: number | string): Observable<CourseCommentsResponse> {
        return this.http.get<CourseCommentsResponse>(`${this.baseUrl}/proposals/course/${courseId}/comments`);
    }

    postponeCourse(
        courseId: number | string,
        payload: {
            startDate: Date;
            endDate: Date;
            lastDateOfEnrollment: Date;
            reasonOfDateChange?: string;
        },
    ): Observable<any> {
        const body = {
            startDate: formatToLocalDateTime(payload.startDate),
            endDate: formatToLocalDateTime(payload.endDate),
            lastDateOfEnrollment: formatToLocalDateTime(payload.lastDateOfEnrollment),
            reasonOfDateChange: payload.reasonOfDateChange,
        };

        return this.http.patch(`${this.baseUrl}/course/${courseId}/postpone`, body);
    }

    /**
     * Creates a new topic (skill)
     * @param topicName The name of the topic to create
     * @returns Observable with the created topic
     */
    createTopic(topicName: string): Observable<any> {
        return this.http.post(`${this.baseUrl}/topic`, { name: topicName });
    }

    getYoutubeVideoMetaData(videoUrl: string): Observable<any> {
        return this.http.get(`https://www.youtube.com/oembed?url=${videoUrl}&format=json`);
    }

    uploadFile(file: File, referenceId: string, key: string): Observable<any> {
        const url = `${this.baseUrl}/file-system`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('referenceId', referenceId);
        formData.append('key', key);
        formData.append('baseFolderName', 'COURSE');

        return this.http.post<any>(url, formData);
    }

    /**
     * Duplicates a course by sending the courseId to the courseduplicate endpoint
     * @param courseId The ID of the course to duplicate
     * @returns Observable with the duplicated course response
     */
    duplicateCourse(courseId: number | string): Observable<any> {
        return this.http.post(`${this.baseUrl}/course/${courseId}/duplicate`, {});
    }

    getCoursesByFilter(filter: CourseFilter): Observable<any> { // NOSONAR
        let params = new HttpParams();

        // Mandatory
        if (filter.status && filter.status.length > 0) {
            filter.status.forEach((s: string) => (params = params.append('status', s)));
        } else {
            throw new Error('Status is required');
        }

        // Optional
        if (filter.searchParam) params = params.set('searchParam', filter.searchParam);
        if (filter.isFeedbackForm !== undefined)
            params = params.set('isFeedbackForm', filter.isFeedbackForm.toString());
        if (filter.self !== undefined) // NOSONAR
            params = params.set('self', filter.self.toString()); // NOSONAR
        else params = params.set('self', 'true');
        if (filter.isParent !== undefined) // NOSONAR
            params = params.set('isParent', filter.isParent.toString()); // NOSONAR
        else params = params.set('isParent', 'false');
        if (filter.type) params = params.set('type', filter.type);
        if (filter.courseStartDate) params = params.set('courseStartDate', filter.courseStartDate);
        if (filter.courseEndDate) params = params.set('courseEndDate', filter.courseEndDate);
        if (filter.isPublished !== undefined) params = params.set('isPublished', filter.isPublished.toString());
        if (filter.page !== undefined) params = params.set('page', filter.page.toString());
        if (filter.size !== undefined) params = params.set('size', filter.size.toString());
        if (filter.sortField) params = params.set('sortField', filter.sortField);
        if (filter.sortDirection) params = params.set('sortDirection', filter.sortDirection);
        return this.http.get<MyCourse[]>(`${this.baseUrl}/course/courses/get-Course`, { params });
    }

    /**
     * Deletes a course or program by ID
     * @param id The ID of the course or program to delete
     * @returns Observable with the delete response
     */
    deleteCourseOrProgram(id: string | number, comments: string = ''): Observable<any> {
        return this.http.delete(`${this.baseUrl}/course/${id}`, {
            params: { comments },
        });
    }

    /**
     * Posts a comment for a specific course
     * @param courseId The ID of the course
     * @param comment The comment text
     * @returns Observable with the post response
     */
    postCourseComment(courseId: number | string, comment: string): Observable<any> {
        const payload = {
            courseId,
            comment,
        };
        return this.http.post(`${this.baseUrl}/proposals/comments`, payload);
    }

    //Get courses in filter by types
    getCourseFilterTypes(): Observable<any> {
        return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/program`);
    }
}
